import { SelectOption } from '@agilox/ui-common';
import {
	AfterViewInit,
	Component,
	computed,
	contentChild,
	effect,
	inject,
	input,
	model,
	output,
	signal,
	untracked,
	viewChild,
	ViewEncapsulation,
	WritableSignal,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { DropdownDirective } from '../dropdown/directives';
import {
	SelectButtonComponent,
	SelectOptionListComponent,
	SelectSearchComponent,
} from './components';
import { SelectCustomOptionDirective } from './directives';
import { CompareFnType } from './types/compareFn.type';
import { SearchFn } from './types/searchFn.type';

import { SelectTranslationService } from '@agilox/common';

/**
 * Select Component:
 * Usage:
 * There are two ways to pass the options to the select component:
 * 1. Pass the options as an array of SelectOption objects to the options input
 * 2. You can implement a custom template for the options, by using the SelectCustomOptionDirective
 * in the parent component, and passing the options as an array of objects to the options input.
 */

@Component({
	selector: 'ui-select',
	templateUrl: './select.component.html',
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class SelectComponent implements FormValueControl<any>, AfterViewInit {
	private translationService = inject(SelectTranslationService);

	showWhenValid = input<boolean>(false);
	placeholder = input<string>('select.placeholder');
	multiple = input<boolean>(false);
	fieldToCompare = input<string>('');
	inputId = input<string>('');
	invalid = input<boolean>(false);
	clearEnabled = input<boolean>(false);
	groupSelectable = input<boolean>(false);
	searchEnabled = input<boolean>(false);
	tabindex = input<number>(0);

	/**
	 * Needs to be set to true if the parent component is using the search input
	 * used to determine if this component should filter the options or not
	 */
	parentSearch = input<boolean>(false);

	/**
	 * If true, the dropdown will not have a fixed width, but will be as wide as the longest option
	 */
	fullDropdownWidth = input<boolean>(false);

	/**
	 * Can be used to disable the select all button
	 * the button is however only shown when the multiple input is true
	 */
	selectAllEnabled = input<boolean>(true);

	/** The search function can be overwritten by passing a function **/
	searchFn = input<SearchFn | undefined>();

	maxSelections = input<number | undefined>();
	maxSelectionsText = input<string>('');
	loading = input<boolean>(false);

	/** The standard compareFn can be overwritten by passing a function **/
	compareFn = input<CompareFnType | undefined>();

	options = input([], {
		transform: (options: SelectOption<any>[] | undefined) => {
			if (!options) {
				return [];
			}
			return options as SelectOption<any>[];
		},
	});

	translatedPlaceholder = computed(() => {
		return this.translationService.translate(this.placeholder());
	});

	translatedSearchPlaceholder = computed(() => {
		return this.translationService.translate('general.search');
	});

	translatedDeselectAll = computed(() => {
		return this.translationService.translate('select.deselect_all');
	});

	translatedSelectAll = computed(() => {
		return this.translationService.translate('select.select_all');
	});

	translatedSave = computed(() => {
		return this.translationService.translate('general.save');
	});

	translatedAll = computed(() => {
		return this.translationService.translate('general.all');
	});

	translatedNoData = computed(() => {
		return this.translationService.translate('general.no_data');
	});

	translatedMaxSelectionsText = computed(() => {
		if (this.maxSelectionsText() && this.maxSelections()) {
			return this.translationService.translate(this.maxSelectionsText(), {
				max: this.maxSelections(),
			});
		}
		return '';
	});

	constructor() {
		effect(() => {
			if (this.options()) {
				this.setOptions(this.options());
			}
		});

		effect(() => {
			const value = this.value();
			untracked(() => this.applyValue(value));
		});
	}

	ngAfterViewInit() {
		if (this.openByDefault()) {
			setTimeout(() => this.openDropdown(), 0);
		}
	}

	private setOptions(options: SelectOption<any>[]) {
		this.filteredOptions.set(options);
		if (this.cachedValue !== null) {
			const cached = this.cachedValue;
			this.cachedValue = null;
			this.applyValue(cached);
		}
	}

	touched = input<boolean>(false);

	disabled = input<boolean>(false);
	openByDefault = input<boolean>(false);

	touch = output<void>();
	searchChange = output<string>();
	selected = output<any>();
	opened = output<boolean>();
	saved = output<any>();
	scrollEnd = output<void>();

	customOptionTemplate = contentChild<SelectCustomOptionDirective | undefined>(
		SelectCustomOptionDirective
	);

	dropdown = viewChild<DropdownDirective | undefined>(DropdownDirective);
	optionList = viewChild<SelectOptionListComponent | undefined>('optionList');
	searchComponent = viewChild<SelectSearchComponent | undefined>('searchComponent');
	deselectButton = viewChild<SelectButtonComponent | undefined>('deselectButton');
	selectAllButton = viewChild<SelectButtonComponent | undefined>('selectAllButton');
	saveButton = viewChild<SelectButtonComponent | undefined>('saveButton');

	/**
	 * The stored options are what was picked; the shown ones are re-resolved against the live
	 * options so a relabelled option updates, while one that is no longer offered still shows.
	 */
	currentlySelectedValues = computed<SelectOption<any>[] | SelectOption<any>>(() => {
		const selected = this.selectedOptions();
		const options = this.options();

		if (Array.isArray(selected)) {
			return selected.map((option) => this.findOption(option.value, options) ?? option);
		}

		return this.findOption(selected.value, options) ?? selected;
	});

	dropdownOpen = signal<boolean>(false);

	value = model<any>(null);

	/**
	 * Stages the picks made while saveRequired is true; they reach `value` on save.
	 */
	savedValues = signal<SelectOption<any>[]>([]);

	filteredOptions: WritableSignal<SelectOption<any>[]> = signal([]);

	selectedOptions = signal<SelectOption<any>[] | SelectOption<any>>([]);

	/**
	 * Needed in case the value is set before the options are set
	 * @private
	 */
	private cachedValue: any = null;

	// prettier-ignore
	filterOptions = (searchTerm: string): SelectOption<any>[] => {

		if(this.searchFn()) {
			return this.searchFn()!(searchTerm, this.options())
		}

		const searchTermLC = searchTerm.toLowerCase();
		return searchTerm
			? this.options().filter(option => option.title.toLowerCase().includes(searchTermLC))
			: this.options();
	};

	/**
	 * Handles the selection of a value
	 * Array will be passed when there are preselected values, and will be automatically
	 * called when the component is initialized
	 * Option will be null if the field is reset or the value is manually set to null
	 * @param option
	 */
	private applyValue(option: any): void {
		if (option === null || option === undefined) {
			this.resetControls();
		} else {
			if (!this.options().length) {
				if (this.multiple() && Array.isArray(option)) {
					this.cachedValue = option;
				} else if (!this.multiple()) {
					this.cachedValue = option;
				}
				return;
			}
			if (this.multiple()) {
				this.writeMultiValue(option);
			} else {
				this.writeSingleValue(option);
			}
		}
	}

	resetControls() {
		this.cachedValue = null;
		this.savedValues.set([]);
		this.selectedOptions.set([]);
	}

	/**
	 * Handles the selection of a single value, when multiple is false
	 * @param option
	 */
	writeSingleValue(option: any): void {
		this.closeDropdown();

		const selectOption: SelectOption<any> | undefined = this.findOption(option, this.options());
		if (selectOption) {
			this.setValue(selectOption);
			return;
		}

		this.cachedValue = option;
	}

	/**
	 * Handles the selection of multiple values, when multiple is true
	 * An array can be passed to this function, in case the values are already selected
	 * @param option
	 */
	writeMultiValue(option: any): void {
		/**
		 * An array means the values are already selected — either preselected by the parent
		 * or written in from outside — and must be resolved against the options as a set.
		 */
		if (Array.isArray(option)) {
			/**
			 * Find the matching selectOptions
			 */
			const selectOptions: SelectOption<any>[] = [];
			option.forEach((opt: any) => {
				const foundOption = this.findOption(opt, this.options());
				if (foundOption) {
					selectOptions.push(foundOption);
				}
			});
			if (selectOptions.length === 0) {
				/**
				 * Only a non-empty value is worth replaying once the options arrive. Caching an empty
				 * one makes every later options change re-apply it, wiping a pending multi-selection.
				 */
				this.cachedValue = option.length ? option : null;
				this.savedValues.set([]);
				this.selectedOptions.set([]);
				return;
			}

			this.setValue(selectOptions);
			return;
		} else {
			/**
			 * Find the matching selectOption
			 */
			const selectOption: SelectOption<any> | undefined = this.findOption(option, this.options());
			/**
			 * Staged in a temporary variable while saveRequired is true; otherwise the
			 * values reach `value` immediately.
			 */
			const values: SelectOption<any>[] = this.multiple()
				? this.savedValues()
				: this.selectedOptionsAsArray();

			if (selectOption) {
				const optionIndex = values.findIndex(
					(value: SelectOption<any>) => value.title === selectOption.title
				);

				if (optionIndex !== -1) {
					values.splice(optionIndex, 1);
				} else {
					values.push(selectOption);
				}
			}

			if (!this.multiple()) {
				this.setValue(values);
			} else {
				this.savedValues.set([...values]);
			}
		}
	}

	deselectAll() {
		/**
		 * Only deselects the options that are currently shown and not disabled.
		 */
		const set: Set<string> = new Set(
			this.filteredOptions()
				.filter((o) => !o.isDisabled)
				.map((option) => option.title)
		);
		this.savedValues.set(this.savedValues().filter((option) => !set.has(option.title)));
		this.selected.emit(this.savedValues());
	}

	/**
	 * Only selects the options that are currently shown
	 */
	selectAll() {
		this.savedValues.set(this.filteredOptions().filter((option) => !option.isDisabled));
		this.selected.emit(this.savedValues());
	}

	private findOption(option: any, options: SelectOption<any>[]): SelectOption<any> | undefined {
		return options.find((opt) => {
			if (!this.fieldToCompare()) {
				return JSON.stringify(opt.value) === JSON.stringify(option);
			} else {
				if (option) {
					return opt.value[this.fieldToCompare()] === option[this.fieldToCompare()];
				} else {
					return false;
				}
			}
		});
	}

	private setValue(value: SelectOption<any> | SelectOption<any>[]) {
		this.selectedOptions.set(value);
		this.cloneFormValue();
	}

	selectedOptionsList = computed<SelectOption<any>[]>(() => {
		const selected = this.selectedOptions();
		return Array.isArray(selected) ? selected : selected ? [selected] : [];
	});

	private selectedOptionsAsArray(): SelectOption<any>[] {
		const selected = this.selectedOptions();
		return Array.isArray(selected) ? selected : selected ? [selected] : [];
	}

	private parsedValue(): any {
		const selected = this.selectedOptions();
		return Array.isArray(selected)
			? selected.map((option) => option.value)
			: (selected?.value ?? null);
	}

	/** Only a selection the user made reaches the field; applyValue is the field writing in. */
	private commitSelection(): void {
		this.value.set(this.parsedValue());
	}

	optionSelected(option: any) {
		this.touch.emit();
		this.selected.emit(option);
	}

	onOptionListSelected(option: any) {
		this.applyValue(option);
		/**
		 * A multiple select stages picks in `savedValues` and only commits them on save. Writing the
		 * (still empty) value here would come back in through the value effect and clear the staging.
		 */
		if (!this.multiple()) {
			this.commitSelection();
		}
		this.optionSelected(option);
	}

	onSave() {
		this.setValue(this.savedValues() || []);
		this.commitSelection();
		this.toggleDropdown();
		this.saved.emit(this.parsedValue());
	}

	openDropdown() {
		this.dropdown()?.openDropdown();
	}

	toggleDropdown() {
		this.dropdown()?.toggleDropdown();
	}

	closeDropdown() {
		this.dropdown()?.closeDropdown();
	}

	onDropdownStateChange(open: boolean) {
		if (open) {
			this.cloneFormValue();
			this.filteredOptions.set(this.options());
			this.opened.emit(true);
			setTimeout(() => {
				if (this.searchEnabled()) {
					this.searchComponent()?.focus();
				} else {
					const optionListElement = this.optionList()?.optionsList()?.nativeElement;
					if (optionListElement) {
						optionListElement.focus();
					}
				}
				// Scroll to the selected option when dropdown opens
				this.optionList()?.scrollToSelected();
			}, 0);
		} else {
			if (this.dropdownOpen() && !open) {
				this.focus();
			}
			this.opened.emit(false);
			this.searchChange.emit('');
		}
		this.dropdownOpen.set(open);
	}

	cloneFormValue() {
		if (this.multiple()) {
			this.savedValues.set(JSON.parse(JSON.stringify(this.selectedOptionsAsArray())));
		}
	}

	onSearch(val: string) {
		this.searchChange.emit(val);
		if (!this.parentSearch()) {
			this.filteredOptions.set(this.filterOptions(val));
		}
	}

	clearSingleSelection(): void {
		this.resetControls();
		this.value.set(null);
	}

	onKeyPressed(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.toggleDropdown();
		}
	}

	focusOptionList(): void {
		setTimeout(() => {
			const optionListElement = this.optionList()?.optionsList()?.nativeElement;
			if (optionListElement) {
				optionListElement.focus();
			}
		}, 0);
	}

	focusButtons(): void {
		setTimeout(() => {
			// Focus the first available button in order of priority
			if (this.multiple()) {
				const deselectBtn = this.deselectButton();
				const selectAllBtn = this.selectAllButton();
				const saveBtn = this.saveButton();

				// Try to focus deselect button first, then select all, then save
				const targetButton = deselectBtn || selectAllBtn || saveBtn;
				if (targetButton && typeof targetButton.focus === 'function') {
					targetButton.focus();
				}
			}
		}, 0);
	}

	onScrollEnd() {
		this.scrollEnd.emit();
	}

	focus(): void {
		setTimeout(() => {
			const selectInputElement = document.getElementById(this.inputId());
			if (selectInputElement) {
				selectInputElement.focus();
			}
		}, 0);
	}
}
